import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { Button, Col, Divider, Form, Modal, Row, Select } from 'antd';
import CustomSelect from '../CustomSelect/CustomSelect';
import { ReactComponent as Decrease } from '../../assets/decrease-icon.svg';
import { ReactComponent as Increase } from '../../assets/increase-icon.svg';

import CustomCheckbox from '../CustomCheckbox/CustomCheckbox';
import { textSort } from '../../helpers/textSort';
import { AssetTypes } from '../../pages/AdminInstruments/AdminInstruments';

import './style.scss';
import { decimalSeparator } from '../../helpers/decimalSeparator';

const ModalFilterInsrt = ({
  assets,
  currencyList,
  setFilter,
  openFilter,
  handleCloseFilter,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation('');

  const [feeMinMax, setFeeMinMax] = useState({ min: '', max: '' });
  const [sharePrice, setSharePrice] = useState({ min: '', max: '' });

  const [category, setCategory] = useState(null);

  const initVAlue = {
    category: ['All'],
    assetClass: ['All'],
    currency: ['All'],
    vehicleType: ['All'],
    hight: true,
    medium: true,
    none: true,
  };

  useEffect(() => {
    form.setFieldsValue(initVAlue);
  }, []);

  const vehicleTypeArray = () => {
    let data = [];
    for (const [label, value] of Object.entries(AssetTypes)) {
      data.push({ label, value });
    }
    return data;
  };

  const createAssetCategory = useMemo(() => {
    const category = ['All'];
    assets.forEach(
      (i) =>
        !category.includes(i?.financialAssetCategory?.name) &&
        category.push(i?.financialAssetCategory?.name),
    );
    return category?.sort((a, b) => textSort(a, b));
  }, [assets]);

  useEffect(() => {
    setCategory(createAssetCategory);
  }, [assets]);

  const handleDecrease = (target) => {
    const getValue = (val) => {
      const num = parseFloat(val.replace(',', '.'));
      return isNaN(num) ? 0 : num;
    };
    if (target === 'fee-min') {
      const min = Math.max(getValue(feeMinMax.min) - 0.1, 0);
      setFeeMinMax({
        ...feeMinMax,
        min: min.toFixed(2).replace('.', decimalSeparator()),
      });
    }
    if (target === 'fee-max') {
      const max = Math.max(getValue(feeMinMax.max) - 0.1, 0);
      setFeeMinMax({
        ...feeMinMax,
        max: max.toFixed(2).replace('.', decimalSeparator()),
      });
    }
    if (target === 'price-min') {
      const min = Math.max(getValue(sharePrice.min) - 0.1, 0);
      setSharePrice({
        ...sharePrice,
        min: min.toFixed(2).replace('.', decimalSeparator()),
      });
    }
    if (target === 'price-max') {
      const max = Math.max(getValue(sharePrice.max) - 0.1, 0);
      setSharePrice({
        ...sharePrice,
        max: max.toFixed(2).replace('.', decimalSeparator()),
      });
    }
  };

  const handleIncrease = (target) => {
    const getValue = (val) => {
      const num = parseFloat(val.replace(',', '.'));
      return isNaN(num) ? 0 : num;
    };
    if (target === 'fee-min') {
      const min = getValue(feeMinMax.min) + 0.1;
      setFeeMinMax({
        ...feeMinMax,
        min: min.toFixed(2).replace('.', decimalSeparator()),
      });
    }
    if (target === 'fee-max') {
      const max = getValue(feeMinMax.max) + 0.1;
      setFeeMinMax({
        ...feeMinMax,
        max: max.toFixed(2).replace('.', decimalSeparator()),
      });
    }
    if (target === 'price-min') {
      const min = getValue(sharePrice.min) + 0.1;
      setSharePrice({
        ...sharePrice,
        min: min.toFixed(2).replace('.', decimalSeparator()),
      });
    }
    if (target === 'price-max') {
      const max = getValue(sharePrice.max) + 0.1;
      setSharePrice({
        ...sharePrice,
        max: max.toFixed(2).replace('.', decimalSeparator()),
      });
    }
  };

  const isValidInput = (input) => {
    // Разрешаем только числа с точкой или запятой, не более одной точки/запятой
    return /^(\d+([.,]\d*)?)?$/.test(input);
  };

  const formatNumberForInput = (value) => {
    if (value === null || value === undefined) return '';
    return value
      .replace('.', decimalSeparator())
      .replace(',', decimalSeparator());
  };

  const parseInputValue = (input) => {
    if (!input) return '';
    const normalized = input.replace(',', '.');
    const num = Number(normalized);
    return isNaN(num) ? '' : normalized;
  };

  const handleInputChange = (event, target) => {
    const { value } = event.target;
    if (!isValidInput(value)) return; // Только валидные строки

    if (target === 'fee-min') setFeeMinMax({ ...feeMinMax, min: value });
    if (target === 'fee-max') setFeeMinMax({ ...feeMinMax, max: value });
    if (target === 'price-min') setSharePrice({ ...sharePrice, min: value });
    if (target === 'price-max') setSharePrice({ ...sharePrice, max: value });
  };

  const handleRest = () => {
    form.resetFields();
    setFeeMinMax({ min: '', max: '' });
    setSharePrice({ min: '', max: '' });
    form.setFieldsValue(initVAlue);
    setFilter(null);
  };

  const handleCancel = () => {
    handleCloseFilter();
  };

  const handleChangeSelect = () => {
    const values = form.getFieldsValue();
    if (values?.category.length > 1)
      values.category = values.category.filter((i) => i !== 'All');
    if (values?.category.length === 0) values.category = ['All'];
    if (values?.assetClass.length > 1)
      values.assetClass = values.assetClass.filter((i) => i !== 'All');
    if (values?.assetClass.length === 0) values.assetClass = ['All'];
    if (values?.currency.length > 1)
      values.currency = values.currency.filter((i) => i !== 'All');
    if (values?.currency.length === 0) values.currency = ['All'];
    if (values?.vehicleType.length > 1)
      values.vehicleType = values.vehicleType.filter((i) => i !== 'All');
    if (values?.vehicleType.length === 0) values.vehicleType = ['All'];
    form.setFieldsValue(values);
  };

  const onSubmit = () => {
    const value = form.getFieldsValue();
    const configFilter = {
      ...value,
      fee: {
        min: parseInputValue(feeMinMax.min),
        max: parseInputValue(feeMinMax.max),
      },
      price: {
        min: parseInputValue(sharePrice.min),
        max: parseInputValue(sharePrice.max),
      },
    };
    setFilter(configFilter);
    handleCloseFilter();
  };

  return (
    <Modal
      width={700}
      className="filter-modal"
      centered
      open={openFilter}
      onCancel={handleCancel}
      footer={false}
      zIndex={1500}
    >
      <Form
        form={form}
        name="filter-config"
        layout="vertical"
        onFinish={onSubmit}
      >
        <div>
          <Row className="profile-data-row">
            <Col span={24}>
              <Form.Item
                label={t('ASSET_CATEGORY')}
                name="category"
                required={false}
                className="full-width"
              >
                <CustomSelect
                  width={'100%'}
                  mode={'multiple'}
                  maxTagCount="responsive"
                  maxTagTextLength={12}
                  onChange={handleChangeSelect}
                  className="custom-select"
                >
                  {category?.map((i) => (
                    <Select.Option key={uuidv4()} value={i}>
                      {i}
                    </Select.Option>
                  ))}
                </CustomSelect>
              </Form.Item>
            </Col>
          </Row>
          <Divider className="filter-divider" />
          <Row className="profile-data-row">
            <Col span={24}>
              <Form.Item
                label={t('ASSET_CLASS')}
                name="assetClass"
                required={false}
                className="full-width"
              >
                <CustomSelect
                  width={'100%'}
                  mode={'multiple'}
                  maxTagCount="responsive"
                  className="custom-select"
                  maxTagTextLength={12}
                  onChange={handleChangeSelect}
                >
                  <Select.Option value="All">All</Select.Option>
                  {assets
                    ?.slice()
                    ?.sort((a, b) => textSort(a.name, b.name))
                    ?.map((i) => (
                      <Select.Option key={i.ID} value={i.name}>
                        {i.name}
                      </Select.Option>
                    ))}
                </CustomSelect>
              </Form.Item>
            </Col>
          </Row>
          <Divider className="filter-divider" />
          <Row className="profile-data-row">
            <Col span={24}>
              <Form.Item
                label={t('INVESTMENT_VEHICLE_TYPE')}
                name="vehicleType"
                required={false}
                className="full-width"
              >
                <CustomSelect
                  width={'100%'}
                  mode={'multiple'}
                  maxTagCount="responsive"
                  className="custom-select"
                  maxTagTextLength={12}
                  onChange={handleChangeSelect}
                >
                  <Select.Option key={0} value="All">
                    All
                  </Select.Option>
                  {vehicleTypeArray()?.map((i) => (
                    <Select.Option key={i.label} value={i.value}>
                      {i.label}
                    </Select.Option>
                  ))}
                </CustomSelect>
              </Form.Item>
            </Col>
          </Row>
          <Divider className="filter-divider" />
          <Row justify={'space-between'} style={{ marginBottom: '20px' }}>
            <Col className="filter-control-title">{t('FEE')}, %</Col>
            <Col className="filter-control-wrapper">
              <div className="controll-wrapper">
                <div
                  className="controll-decrease"
                  onClick={() => handleDecrease('fee-min')}
                >
                  <Decrease />
                </div>
                <input
                  type={'text'}
                  className="control-min-value"
                  value={formatNumberForInput(feeMinMax.min)}
                  onChange={(event) => handleInputChange(event, 'fee-min')}
                />
                <div
                  className="controll-increase"
                  onClick={() => handleIncrease('fee-min')}
                >
                  <Increase />
                </div>
              </div>
              <span className="controll-delimiter">-</span>
              <div className="controll-wrapper">
                <div
                  className="controll-increase"
                  onClick={() => handleDecrease('fee-max')}
                >
                  <Decrease />
                </div>
                <input
                  type={'text'}
                  className="control-max-value"
                  value={formatNumberForInput(feeMinMax.max)}
                  onChange={(event) => handleInputChange(event, 'fee-max')}
                />
                <div
                  className="controll-increase"
                  onClick={() => handleIncrease('fee-max')}
                >
                  <Increase />
                </div>
              </div>
            </Col>
          </Row>
          <Divider className="filter-divider" />
          <Row align={'top'} justify={'space-between'}>
            <Col
              style={{ display: 'flex', alignItems: 'flex-start' }}
              span={12}
            >
              <Form.Item
                label={t('CURRENCY')}
                name="currency"
                className="full-width"
                required={false}
              >
                <CustomSelect
                  width={'100%'}
                  mode={'multiple'}
                  maxTagCount="responsive"
                  onChange={handleChangeSelect}
                  className="custom-select"
                >
                  <Select.Option value="All">All</Select.Option>
                  {currencyList
                    ?.sort((a, b) => textSort(a.name, b.name))
                    ?.map((i) => (
                      <Select.Option key={i.id} value={i.id}>
                        {i.name}
                      </Select.Option>
                    ))}
                </CustomSelect>
              </Form.Item>
            </Col>
            <Col>
              <p className="fillter-price-title">{t('SHARE_PRICE')}</p>
              <div className="filter-control-wrapper">
                <div className="controll-wrapper">
                  <div
                    className="controll-decrease"
                    onClick={() => handleDecrease('price-min')}
                  >
                    <Decrease />
                  </div>
                  <input
                    type={'text'}
                    className="control-min-value"
                    value={formatNumberForInput(sharePrice.min)}
                    onChange={(event) => handleInputChange(event, 'price-min')}
                  />
                  <div
                    className="controll-increase"
                    onClick={() => handleIncrease('price-min')}
                  >
                    <Increase />
                  </div>
                </div>
                <span className="controll-delimiter">-</span>
                <div className="controll-wrapper">
                  <div
                    className="controll-increase"
                    onClick={() => handleDecrease('price-max')}
                  >
                    <Decrease />
                  </div>
                  <input
                    type={'text'}
                    className="control-max-value"
                    value={formatNumberForInput(sharePrice.max)}
                    onChange={(event) => handleInputChange(event, 'price-max')}
                  />
                  <div
                    className="controll-increase"
                    onClick={() => handleIncrease('price-max')}
                  >
                    <Increase />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <Divider className="filter-divider" />
          <Row
            justify={'space-between'}
            align={'middle'}
            style={{ marginBottom: '20px' }}
          >
            <Col className="filter-control-title">{t('DIVERSIFICATION')}</Col>
            <Col className="filter-control-wrapper">
              <Form.Item
                name={'hight'}
                valuePropName="checked"
                className="filter-check-item"
              >
                <CustomCheckbox>{t('HIGH')}</CustomCheckbox>
              </Form.Item>
              <Form.Item
                name={'medium'}
                valuePropName="checked"
                className="filter-check-item"
              >
                <CustomCheckbox>{t('MEDIUM')}</CustomCheckbox>
              </Form.Item>
              <Form.Item
                name={'none'}
                valuePropName="checked"
                className="filter-check-item"
              >
                <CustomCheckbox>{t('NONE')}</CustomCheckbox>
              </Form.Item>
            </Col>
          </Row>
          <Divider className="filter-divider" />
          <Row className="modal-newportfolio-button-wrapper">
            <Button
              type="text"
              className="modal-action-btn cancel-btn"
              onClick={handleRest}
            >
              {t('RESET')}
            </Button>
            <Button
              type="default"
              className="modal-action-btn save-btn"
              onClick={onSubmit}
            >
              {t('APPLY')}
            </Button>
          </Row>
        </div>
      </Form>
    </Modal>
  );
};

export default ModalFilterInsrt;

ModalFilterInsrt.propTypes = {
  assets: PropTypes.array,
  currencyList: PropTypes.array,
  openFilter: PropTypes.bool,
  handleCloseFilter: PropTypes.func,
  setFilter: PropTypes.func,
};
